import React from 'react'
import { MOBILE_BREAKPOINT } from './useIsMobile'

export const KEYBOARD_INSET_VAR = '--keyboard-inset'

// клавиатура ниже этого порога — не клавиатура, а свернувшаяся панель браузера
const KEYBOARD_MIN_HEIGHT = 120

// чекбоксы и кнопки клавиатуру не открывают
const NON_TEXT_INPUT_TYPES = [
  'button',
  'checkbox',
  'color',
  'file',
  'radio',
  'range',
  'reset',
  'submit'
]

const isTextFieldFocused = () => {
  const element = document.activeElement as HTMLElement | null
  if (!element) return false
  if (element.isContentEditable || element.tagName === 'TEXTAREA') return true

  return (
    element.tagName === 'INPUT' &&
    !NON_TEXT_INPUT_TYPES.includes((element as HTMLInputElement).type)
  )
}

// Клавиатура ужимает видимую область, а раскладка страницы остаётся прежней,
// и низ страницы вместе с таббаром упирается в её новую границу. Ниже границы
// страницу уже не рисуют — там панели браузера, поэтому при открытой
// клавиатуре таббар уводим целиком под них: замеряем расстояние от его верха
// до низа видимой области и отдаём стилям переменной. Освободившуюся высоту
// забирает содержимое. Замер по самому таббару — так неважно, чем именно
// браузер ограничил раскладку
export const useKeyboardInset = (elementRef: React.RefObject<HTMLElement>) => {
  React.useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    let inset = 0

    const apply = (value: number) => {
      inset = value
      document.documentElement.style.setProperty(
        KEYBOARD_INSET_VAR,
        `${value}px`
      )
    }

    const update = () => {
      const rect = elementRef.current?.getBoundingClientRect()
      const isKeyboardOpen =
        window.innerWidth <= MOBILE_BREAKPOINT &&
        window.innerHeight - viewport.height > KEYBOARD_MIN_HEIGHT &&
        isTextFieldFocused()

      if (!rect?.height || !isKeyboardOpen) {
        if (inset !== 0) apply(0)
        return
      }

      // низ видимой области и верх таббара — оба в координатах раскладки;
      // текущий сдвиг вычитаем, иначе замер считал бы его заново
      const visibleBottom = viewport.offsetTop + viewport.height
      const target = Math.max(
        0,
        Math.round(visibleBottom - (rect.top - inset))
      )

      if (target !== inset) apply(target)
    }

    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    // focusout не слушаем: при переходе между полями таббар успел бы моргнуть,
    // а закрытие клавиатуры и без него приходит событием visualViewport
    document.addEventListener('focusin', update)

    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      document.removeEventListener('focusin', update)
      apply(0)
    }
  }, [elementRef])
}
